/** Styles */
import './CreateDepositAndReceiptDialog.scss';
/** Templates */
import createDepositAndReceiptDialogTemplate from './CreateDepositAndReceiptDialog.template.html';

/*******************************************************************************
 * CreateDepositAndReceiptDialogService */
/**
 * Service that opens the Create Deposit and Receipt dialog.
 *******************************************************************************/
/* @ngInject */ export function CreateDepositAndReceiptDialogService(
  $uibModal,
  MissingReceiptsAdminService
) {
  /**
   * Loads the deposit/receipt preview for a web order. When a POS sale's card payments don't cover
   * its minimum payment (the items that don't allow payments), the server builds nothing and just
   * reports the shortfall - the dialog then warns the admin before showing anything else (see
   * CreateDepositAndReceiptController).
   */
  this.loadOrderInfo = (webOrderID) =>
    MissingReceiptsAdminService.getDepositAndReceiptInfo(webOrderID, null);

  this.show = (selectedRow, orderInfo) => {
    return $uibModal.open({
      backdrop: 'static',
      component: 'createDepositAndReceipt',
      resolve: {
        selectedRow: () => selectedRow,
        orderInfo: () => orderInfo
      },
      size: 'lg',
      windowClass: 'create-deposit-and-receipt-modal'
    }).result;
  };
}

/**
 * Create Deposit and Receipt Component
 */
export const createDepositAndReceiptComponent = {
  bindings: {
    resolve: '<',
    close: '&',
    dismiss: '&'
  },
  template: createDepositAndReceiptDialogTemplate,
  controller: CreateDepositAndReceiptController
};

/*******************************************************************************
 * CreateDepositAndReceiptController */
/**
 * Controller for the Create Deposit and Receipt dialog component.
 *******************************************************************************/
/* @ngInject */ function CreateDepositAndReceiptController($injector) {
  const $ctrl = this;
  const $svcs = { $ctrl };

  $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));

  // Initialize component data
  $ctrl.$onInit = function () {
    $ctrl.selectedRow = $ctrl.resolve.selectedRow;
    $ctrl.TENDERS = TENDERS;

    /**
     * A POS sale whose card payments don't cover its minimum payment can't be created as is - the
     * admin sees the shortfall warning first ('warning'), and only if they go on, picks the tender
     * the bookkeeper says paid it ('tender'). Null once there's a receipt to preview.
     */
    $ctrl.shortfallStep = null;
    $ctrl.tender = { type: null, name: '', number: '', amount: null };
    $ctrl.remainderTender = null;

    $ctrl.setOrderInfo($ctrl.resolve.orderInfo);
    if ($ctrl.orderInfo.shortfall > 0 && !$ctrl.receipt) {
      $ctrl.shortfallStep = 'warning';
      // The tender pays the shortfall unless the admin sets more.
      $ctrl.tender.amount = $ctrl.orderInfo.shortfall;
    }
  };
}

/** Tenders the admin can record a POS shortfall in - see RemainderTender (C#). */
const TENDERS = [
  { type: 'cash', label: 'Cash' },
  { type: 'check', label: 'Check' },
  { type: 'studentFunds', label: 'Student Funds' },
  { type: 'manualCard', label: 'Manual Card' }
];

/*******************************************************************************
 * initCtrl */
/**
 * Initializes the controller object and its handlers.
 *******************************************************************************/
/* @ngInject */ function initCtrl($ctrl, $injector, $svcs) {
  Object.assign($ctrl, {
    applyTender: $injector.invoke(applyTender, null, $svcs),
    canApplyTender: $injector.invoke(canApplyTender, null, $svcs),
    formatPageName: $injector.invoke(formatPageName, null, $svcs),
    postDepositAndReceipt: $injector.invoke(postDepositAndReceipt, null, $svcs),
    receiptFor: $injector.invoke(receiptFor, null, $svcs),
    setOrderInfo: $injector.invoke(setOrderInfo, null, $svcs),
    tenderAllowed: $injector.invoke(tenderAllowed, null, $svcs),
    tenderLabel: $injector.invoke(tenderLabel, null, $svcs)
  });
}

/*******************************************************************************
 * setOrderInfo */
/**
 * Shows the deposit/receipt info the server built for the web order.
 *******************************************************************************/
/* @ngInject */ function setOrderInfo($ctrl) {
  return function (orderInfo) {
    $ctrl.orderInfo = orderInfo;
    $ctrl.receipt = orderInfo.receipt;
    $ctrl.deposit = orderInfo.deposit;
    $ctrl.webOrder = orderInfo.webOrder;
    // What the shortfall is measured against: the cards plus the shortfall make the minimum payment.
    $ctrl.minimumPayment = orderInfo.cardTotal + orderInfo.shortfall;
    $ctrl.owedOnPaymentItems = orderInfo.webOrder.total - $ctrl.minimumPayment;
    // Most the tender can pay - what the card payments left unpaid on the sale.
    $ctrl.maxTenderAmount = orderInfo.webOrder.total - orderInfo.cardTotal;
    // If the deposit has an ID, then it already exists in our system.
    $ctrl.hasDepositRecord = !!(orderInfo.deposit && orderInfo.deposit.id);
    /**
     * Payments against unpaid items/fines. One that pays more than is still owed (the debt was
     * paid off some other way since the sale) is only flagged - the receipt can still be created.
     */
    $ctrl.debtPayments = orderInfo.debtPayments || [];
    $ctrl.hasOverpaidDebt = $ctrl.debtPayments.some(
      (debtPayment) =>
        debtPayment.balanceAfter != null && debtPayment.balanceAfter < 0
    );
    // Destiny fine payments are only recorded on the receipt - never sent to Destiny.
    $ctrl.hasDestinyPayment = $ctrl.debtPayments.some(
      (debtPayment) => debtPayment.destiny
    );
  };
}

/*******************************************************************************
 * tenderAllowed */
/**
 * Returns whether `type` can pay the shortfall: Student Funds only when the
 * sale's individual has enough, Manual Card only when the org has a manual
 * card processor.
 *******************************************************************************/
/* @ngInject */ function tenderAllowed($ctrl) {
  return function (type) {
    const { studentFunds, manualCardAllowed, shortfall } = $ctrl.orderInfo;
    if (type === 'studentFunds')
      return studentFunds != null && studentFunds >= shortfall;
    if (type === 'manualCard') return !!manualCardAllowed;
    return true;
  };
}

/*******************************************************************************
 * canApplyTender */
/**
 * Returns whether the picked tender has everything it needs: an amount from
 * the shortfall up to what the cards left unpaid (and no more than the
 * student's funds), a check number and name for a check, the last four
 * digits for a manual card.
 *******************************************************************************/
/* @ngInject */ function canApplyTender($ctrl) {
  return function () {
    const { type, name, number, amount } = $ctrl.tender;
    if (!type || !$ctrl.tenderAllowed(type)) return false;

    if (
      !(amount >= $ctrl.orderInfo.shortfall) ||
      !(amount <= $ctrl.maxTenderAmount)
    )
      return false;
    if (type === 'studentFunds' && amount > $ctrl.orderInfo.studentFunds)
      return false;

    if (type === 'check') return !!(number && name && name.trim());
    if (type === 'manualCard') return /^\d{4}$/.test(number || '');
    return true;
  };
}

/*******************************************************************************
 * applyTender */
/**
 * Builds the receipt again with the picked tender paying the amount set, then
 * shows the deposit/receipt preview. Nothing is saved until the admin creates
 * it.
 *******************************************************************************/
/* @ngInject */ function applyTender(
  Alert,
  BusyScreen,
  MissingReceiptsAdminService,
  $ctrl
) {
  return function () {
    if (!$ctrl.canApplyTender()) return;

    const remainderTender = {
      type: $ctrl.tender.type,
      name: $ctrl.tender.name || null,
      number: $ctrl.tender.number || null,
      amount: Math.round($ctrl.tender.amount * 100) / 100
    };

    BusyScreen.setBusy(true);

    MissingReceiptsAdminService.getDepositAndReceiptInfo(
      $ctrl.webOrder.id,
      remainderTender
    )
      .then((orderInfo) => {
        $ctrl.setOrderInfo(orderInfo);
        $ctrl.remainderTender = remainderTender;
        $ctrl.shortfallStep = null;
      })
      .catch((error) => {
        Alert.error(
          error.parsedResponse
            ? error.parsedResponse.message()
            : error.statusText || 'Unknown error occurred.',
          `${$ctrl.formatPageName()} Error`
        );
      })
      .finally(() => {
        BusyScreen.setBusy(false);
      });
  };
}

/*******************************************************************************
 * tenderLabel */
/**
 * Returns the picked remainder tender's name, with its check or card number.
 *******************************************************************************/
/* @ngInject */ function tenderLabel($ctrl) {
  return function () {
    const tender = $ctrl.remainderTender;
    if (!tender) return '';

    const label = TENDERS.find((t) => t.type === tender.type).label;
    if (tender.type === 'check') return `${label} #${tender.number}`;
    if (tender.type === 'manualCard') return `${label} ending ${tender.number}`;
    return label;
  };
}

/*******************************************************************************
 * formatPageName */
/**
 * Returns the dialog title: Create Receipt when the deposit already exists,
 * otherwise Create Deposit & Receipt.
 *******************************************************************************/
/* @ngInject */ function formatPageName($ctrl) {
  return function () {
    if ($ctrl.shortfallStep) return 'Create Receipt';
    return $ctrl.hasDepositRecord
      ? 'Create Receipt'
      : 'Create Deposit & Receipt';
  };
}

/*******************************************************************************
 * postDepositAndReceipt */
/**
 * Send over the deposit, receipt and webOrder objects to the server so that it
 * can determine what needs to be created and linked for this transaction.
 *******************************************************************************/
/* @ngInject */ function postDepositAndReceipt(
  Alert,
  BusyScreen,
  MissingReceiptsAdminService,
  $ctrl
) {
  return function () {
    /**
     * Sanity check - we should always have all three of these objects at this point, but if any are missing
     * then something went wrong with the data retrieval and we should not attempt to create anything.
     * Alert the user and exit gracefully so that they can try again.
     *
     * Realistically, this should never happen because we have code to prevent the modal from opening if we
     * don't have the necessary data, but this is just an extra layer of protection against unforeseen issues
     * with the data fetching logic.
     */
    const missing = ['receipt', 'deposit', 'webOrder'].filter(
      (key) => !$ctrl[key]
    );
    if (missing.length) {
      Alert.info(
        `Missing record(s): ${missing.join(', ')}. Please close the "${$ctrl.formatPageName()}" dialog and try again.`,
        'Missing Information',
        true
      );
      return;
    }

    BusyScreen.setBusy(true);

    /**
     * remainderTender only tells the server which tender paid a POS shortfall - the server
     * recomputes the amount itself rather than trusting the tender row on this receipt.
     */
    MissingReceiptsAdminService.createDepositAndReceipt(
      $ctrl.receipt,
      $ctrl.deposit,
      $ctrl.webOrder,
      $ctrl.remainderTender
    )
      .then((result) => {
        const label = $ctrl.hasDepositRecord
          ? 'Receipt'
          : 'Deposit and Receipt';
        Alert.success(`${label} #${result.number} created successfully.`);
        $ctrl.close({ $value: result });
      })
      .catch((error) => {
        console.error('Error creating receipt:', error);
        Alert.error(
          error.parsedResponse
            ? error.parsedResponse.message()
            : error.statusText || 'Unknown error occurred.',
          `${$ctrl.formatPageName()} Error`
        );
      })
      .finally(() => {
        BusyScreen.setBusy(false);
      });
  };
}

/*******************************************************************************
 * receiptFor */
/**
 * Returns the "For <purchaser>" label for the receipt, or null if the order has
 * no purchaser.
 *******************************************************************************/
/* @ngInject */ function receiptFor($ctrl) {
  return function () {
    return $ctrl.webOrder.purchaser ? `For ${$ctrl.webOrder.purchaser}` : null;
  };
}
